import { useCallback, useEffect, useRef, useState } from 'react';

import { trackEvent } from '@/modules/analytics';
import { submitSurveyResponses } from '@/modules/api';
import { useOnboardingSteps, useRulesEngine } from '@/modules/rulesEngine';
import { SurveyContent, type Answers } from '@/modules/survey';
import { LoadingScreen } from '@/modules/ui';

import { useAssignedScreener } from './hooks/useAssignedScreener';
import { buildSurveyResponsePayload } from './utils/buildSurveyResponsePayload';

type LegacyAssignedScreenerFlowProps = {
  /** Screener keys to exclude from the fetch (the migrated legacy keys in the
   * test arm, where those screeners were already answered via dynamic assessment). */
  excludeKeys?: readonly string[];
};

/**
 * Legacy dynamic survey — backend-assigned screeners. Consumes the list one at a
 * time (first only), renders the screener's questions (see SurveyContent / stylos
 * SurveyPage), submits answers to /surveycustomerresponse, then re-fetches the
 * list until it's empty — the backend re-sorts after each answer. An empty list,
 * or any fetch/submit error (400 / server), advances to the next onboarding step.
 *
 * Runs in the control arm (serving every screener) and, in the test arm, AFTER
 * the dynamic pass — there it's given `excludeKeys` so the already-answered
 * migrated screeners are stripped, leaving any screener dynamic assessment
 * doesn't support yet (today: none — the list is typically empty). Either way,
 * finishing this flow ends the survey step.
 */
export function LegacyAssignedScreenerFlow({ excludeKeys }: LegacyAssignedScreenerFlowProps) {
  const { user } = useRulesEngine();
  const { goToNextStep } = useOnboardingSteps();
  const { content, isLoading, isExhausted, error, next } = useAssignedScreener({ excludeKeys });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const startedUriRef = useRef<string | null>(null);

  const advanceToPrograms = useCallback(
    () => goToNextStep({ from: '/step/assigned-screeners' }),
    [goToNextStep]
  );

  const onContinue = useCallback(
    async (answers: Answers) => {
      const userUuid = user?.uuid;
      if (!content || !userUuid) {
        advanceToPrograms();
        return;
      }
      trackEvent(`assigned-screeners-click-continue-${content.internalName}`, {
        uuid: userUuid,
        screenerName: content.internalName,
        programUnitInstanceUuid: content.programUnitInstanceUri,
      });
      try {
        setIsSubmitting(true);
        await submitSurveyResponses(
          buildSurveyResponsePayload({
            answers,
            questions: content.questions,
            userUuid,
            questionGroupUri: content.questionGroupUri,
            programUnitInstanceUri: content.programUnitInstanceUri,
          })
        );
        // Loop: reload for the next screener (re-sorted by the backend).
        next();
      } catch {
        // Submit failure → skip to program selection (Sparta's failure path).
        advanceToPrograms();
      } finally {
        setIsSubmitting(false);
      }
    },
    [content, user?.uuid, next, advanceToPrograms]
  );

  // No more screeners, or a load error → leave the survey for programs.
  const shouldAdvance = !isLoading && (isExhausted || error != null);
  useEffect(() => {
    if (shouldAdvance) advanceToPrograms();
  }, [shouldAdvance, advanceToPrograms]);

  // Fire "screener-started" once per screener instance, as each loads.
  useEffect(() => {
    if (content && user?.uuid && startedUriRef.current !== content.programUnitInstanceUri) {
      startedUriRef.current = content.programUnitInstanceUri;
      trackEvent(`screener-started-${content.internalName}`, {
        uuid: user.uuid,
        screenerName: content.internalName,
        screenerUuid: content.programUnitInstanceUri,
      });
    }
  }, [content, user?.uuid]);

  // While loading, advancing, or between screeners, hold on the spinner.
  if (isLoading || shouldAdvance || !content) {
    return <LoadingScreen />;
  }

  return (
    <SurveyContent
      key={content.programUnitInstanceUri}
      questions={content.questions}
      onContinue={onContinue}
      isSubmitting={isSubmitting}
      testIDPrefix='assigned-screeners'
    />
  );
}
